import { format } from '@querodelivery/qd-date-helper';

import type { IMotoboy } from '@/Interfaces/Data';
import { cnpjLabel, cpfLabel, normalizeStatus } from '@/Utils/FnUtils';
import WhatsappNumber from '../../WhatsappNumber/WhatsappNumber';
import useAddressModal from '@/hooks/address/useAddressModal';

import styles from './styles.module.scss';
import { useAppDispatch } from '@/_store/hooks';
import {
  setFareIdToShow,
  showFranchiseModal,
} from '@/_store/modules/franchise';
import { ENUM_DELIVERYMAN_STATUS } from '@/_constants/enums';
import { VEHICLE_LABELS } from '@/_features/motoboys/deliveryman-form/constants';
import { useCities } from '@/_hooks/use-cities';

interface Props {
  motoboy: IMotoboy;
}

function MotoboyInfo({ motoboy }: Props) {
  const dispatch = useAppDispatch();

  const { showAddressModal } = useAddressModal();
  const { getCityInfo } = useCities();

  const franchise = getCityInfo(motoboy.municipioId);

  return (
    <table className={styles.tableContainer}>
      <tr>
        <span>Id </span>
        <strong>{motoboy._id}</strong>
      </tr>
      <tr>
        <span>Versão do app</span>
        <strong>{motoboy.appVersion || '-'}</strong>
      </tr>
      <tr>
        <span>Sistema operacional (SO)</span>
        <strong>
          {motoboy.mobileOS || '-'} {motoboy.mobileOSVersion || '-'}
        </strong>
      </tr>
      <tr>
        <span>Status </span>
        <strong>{normalizeStatus(motoboy.status)}</strong>
      </tr>
      <tr>
        <span>Last State </span>
        <strong>{normalizeStatus(motoboy.lastState.page)}</strong>
      </tr>
      {motoboy.lastLocalizacao && motoboy.lastLocalizacao.coordinates ? (
        <tr>
          <span>Última Localização</span>
          <strong>
            <a
              onClick={() =>
                showAddressModal({ coordinates: motoboy.lastLocalizacao })
              }
            >
              Abrir
            </a>
          </strong>
        </tr>
      ) : null}
      <tr>
        <span>Data de criação </span>
        <strong>{format(new Date(motoboy.createdAt), 'dd/MM/yyyy')}</strong>
      </tr>
      <tr>
        <span>Aprovado em </span>
        <strong>
          {motoboy.approvedAt
            ? format(new Date(motoboy.approvedAt), 'dd/MM/yyyy')
            : ' - '}
        </strong>
      </tr>
      {motoboy.deletedAt && (
        <tr>
          <span>Excluído em </span>
          <strong>{format(new Date(motoboy.deletedAt), 'dd/MM/yyyy')}</strong>
        </tr>
      )}
      <tr>
        <span>Celular </span>
        <WhatsappNumber
          number={
            motoboy.status === ENUM_DELIVERYMAN_STATUS.EXCLUIDO
              ? motoboy.oldCelular || ''
              : motoboy.celular
          }
        />
      </tr>
      {motoboy.email && (
        <tr>
          <span>Email </span>
          <strong className={styles.noCapitalize}>{motoboy.email}</strong>
        </tr>
      )}
      {motoboy.cnh && (
        <tr>
          <span>CNH </span>
          <strong>{motoboy.cnh.numero}</strong>
        </tr>
      )}
      {motoboy.cnpj && (
        <tr>
          <span>CNPJ </span>
          <strong>{cnpjLabel(motoboy.cnpj)}</strong>
        </tr>
      )}
      {motoboy.cpf && (
        <tr>
          <span>CPF </span>
          <strong>{cpfLabel(motoboy.cpf)}</strong>
        </tr>
      )}
      {motoboy.civilState && (
        <tr>
          <span>Estado civil </span>
          <strong>{motoboy.civilState.toLocaleLowerCase()}</strong>
        </tr>
      )}
      {motoboy.tipoVeiculo && (
        <tr>
          <span>Veículo</span>
          <strong>{VEHICLE_LABELS[motoboy.tipoVeiculo]}</strong>
        </tr>
      )}
      <tr>
        <span>Tem documento do veículo?</span>
        <strong>{motoboy.documentoVeiculo ? 'Sim' : 'Não'}</strong>
      </tr>

      <tr>
        <span>Franquia </span>
        <a
          onClick={() => {
            dispatch(showFranchiseModal(null));
            dispatch(setFareIdToShow(motoboy.municipioId));
          }}
        >
          {franchise?.nome} - {franchise?.uf}
        </a>
      </tr>

      {motoboy.enderecoResidencia ? (
        <>
          <tr>
            <span>Residência </span>
            <strong>
              <a
                onClick={() =>
                  showAddressModal({ address: motoboy?.enderecoResidencia })
                }
              >
                Abrir
              </a>
            </strong>
          </tr>
          <tr>
            <span>Logradouro </span>
            <strong>{motoboy.enderecoResidencia.logradouro}</strong>
          </tr>
          <tr>
            <span>Número </span>
            <strong>{motoboy.enderecoResidencia.numero}</strong>
          </tr>
          <tr>
            <span>Bairro </span>
            <strong>{motoboy.enderecoResidencia.bairro}</strong>
          </tr>
          <tr>
            <span>CEP </span>
            <strong>{motoboy.enderecoResidencia.cep}</strong>
          </tr>
          <tr>
            <span>Complemento </span>
            <strong>{motoboy.enderecoResidencia.complemento}</strong>
          </tr>
          <tr>
            <span>Referência </span>
            <strong>{motoboy.enderecoResidencia.referencia}</strong>
          </tr>
        </>
      ) : null}
    </table>
  );
}

export default MotoboyInfo;
